import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import TableEdit from "@/components/contatos/TableEdit";
import { Link } from "react-router-dom";
import type { ContactDetails } from "@/hooks/useContactDetails";
import { User, Pencil } from "lucide-react";
import { format } from "date-fns";
import { useTeam } from "@/hooks/useTeam";
import { useCustomFields } from "@/hooks/useCustomFields";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ContactNameProps {
  contact: ContactDetails;
  onUpdate: (updates: Partial<ContactDetails>) => Promise<boolean>;
}

const ContactName: React.FC<ContactNameProps> = ({ contact, onUpdate }) => {
  const { members } = useTeam();
  const { fields: customFieldDefs } = useCustomFields();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: contact.name,
    email: contact.email || '',
    phone: contact.phone || '',
  });
  const [editCustomFields, setEditCustomFields] = useState<Record<string, any>>(
    (contact.custom_fields as Record<string, any>) || {}
  );

  const attributedMember = contact.attribution_id
    ? members.find(m => m.auth_user_id === contact.attribution_id)
    : null;

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };
  return (
    <div className="w-full flex flex-col gap-3 order-1">
      {/* Contact name ---------------------------> */}
      <div className="w-full p-4 flex rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogTrigger asChild>
            <button className="self-end mb-2">
              <TableEdit styles="" contactId={contact.id} />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-800 border-neutral-700">
            <DialogHeader>
              <DialogTitle className="text-neutral-50">Editar Contato</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="edit-name" className="text-neutral-50">Nome</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="bg-neutral-700 border-neutral-600 text-neutral-50"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="edit-email" className="text-neutral-50">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="bg-neutral-700 border-neutral-600 text-neutral-50"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="edit-phone" className="text-neutral-50">Telefone</Label>
                <Input
                  id="edit-phone"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  className="bg-neutral-700 border-neutral-600 text-neutral-50"
                />
              </div>
              {/* Campos Personalizados */}
              {customFieldDefs.filter(f => f.is_active).length > 0 && (
                <div className="flex flex-col gap-3 border-t border-neutral-600 pt-4">
                  <p className="text-sm font-semibold text-neutral-50">Campos Personalizados</p>
                  {customFieldDefs.filter(f => f.is_active).map((field) => (
                    <div key={field.id} className="flex flex-col gap-2">
                      <Label htmlFor={`cf-detail-${field.id}`} className="text-neutral-50">
                        {field.name}{field.is_required ? ' *' : ''}
                      </Label>
                      {field.field_type === 'text' && (
                        <Input
                          id={`cf-detail-${field.id}`}
                          placeholder={field.name}
                          value={editCustomFields[field.name] || ''}
                          onChange={(e) => setEditCustomFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                          className="bg-neutral-700 border-neutral-600 text-neutral-50"
                        />
                      )}
                      {field.field_type === 'number' && (
                        <Input
                          id={`cf-detail-${field.id}`}
                          type="number"
                          placeholder={field.name}
                          value={editCustomFields[field.name] || ''}
                          onChange={(e) => setEditCustomFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                          className="bg-neutral-700 border-neutral-600 text-neutral-50"
                        />
                      )}
                      {field.field_type === 'date' && (
                        <Input
                          id={`cf-detail-${field.id}`}
                          type="date"
                          value={editCustomFields[field.name] || ''}
                          onChange={(e) => setEditCustomFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                          className="bg-neutral-700 border-neutral-600 text-neutral-50 [color-scheme:dark]"
                        />
                      )}
                      {(field.field_type === 'select' || field.field_type === 'multi_select') && (
                        <Select
                          value={editCustomFields[field.name] || ''}
                          onValueChange={(value) => setEditCustomFields(prev => ({ ...prev, [field.name]: value === '__none__' ? '' : value }))}
                        >
                          <SelectTrigger className="bg-neutral-700 border-neutral-600 text-neutral-50 w-full">
                            <SelectValue placeholder={`Selecione ${field.name}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="__none__">Nenhum</SelectItem>
                              {(field.options || []).map((option: string) => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Button
                onClick={async () => {
                  const success = await onUpdate({ ...editFormData, custom_fields: editCustomFields });
                  if (success) setEditDialogOpen(false);
                }}
                className="w-full"
              >
                Salvar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <div className="w-full flex flex-col justify-center items-center">
          {contact.profile_picture ? (
            <img
              src={contact.profile_picture}
              alt={contact.name}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-2xl">
              {contact.name.charAt(0).toUpperCase()}
            </div>
          )}
          <p className="text-2xl text-neutral-50 font-bold text-center mt-3">{contact.name}</p>
          <p className="text-sm font-normal text-neutral-400 mt-1">
            Criado em {format(new Date(contact.created_at), 'dd/MM/yyyy')}
          </p>
        </div>
        <div className="w-full mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Email</p>
            <p className="text-sm font-medium text-neutral-50">{contact.email || 'Não informado'}</p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Telefone</p>
            <p className="text-sm font-medium text-neutral-50">{contact.phone || 'Não informado'}</p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide">WhatsApp ID</p>
            <p className="text-sm font-medium text-neutral-50">{contact.wa_id || 'Não informado'}</p>
          </div>
        </div>
        <div className="w-full mt-6 flex justify-center items-center">
          <Link to={`/chat/${contact.id}`} className="w-full">
            <Button className="w-full">Ir para o chat</Button>
          </Link>
        </div>
      </div>
      {/* Custom fields ----------------------------> */}
      <div className="w-full p-4 flex gap-3 rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <div className="w-full flex justify-between items-center">
          <h3 className="text-base font-semibold text-neutral-50">Campos personalizados</h3>
        </div>
        <div className="w-full flex flex-col gap-3">
          {contact.custom_fields && Object.keys(contact.custom_fields).length > 0 ? (
            Object.entries(contact.custom_fields as Record<string, any>).map(([key, value]) => {
              const fieldDef = customFieldDefs.find(f => f.name === key);
              const displayValue = value === '' || value === null || value === undefined ? 'Não informado' : String(value);
              return (
                <div key={key} className="flex flex-col gap-1">
                  <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide">{key}</p>
                  <p className="text-sm font-medium text-neutral-50">
                    {fieldDef?.field_type === 'date' && value ? format(new Date(value), 'dd/MM/yyyy') : displayValue}
                  </p>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-neutral-500">Nenhum campo personalizado</p>
          )}
        </div>
      </div>

      {/* Attributed Member ----------------------------> */}
      <div className="w-full p-4 flex gap-3 rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <div className="w-full flex justify-between items-center">
          <h3 className="text-base font-semibold text-neutral-50">Responsável</h3>
        </div>
        <div className="w-full flex items-center gap-3">
          {attributedMember ? (
            <>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                {getInitials(attributedMember.name, attributedMember.email)}
              </div>
              <div className="flex flex-col">
                <p className="text-sm font-medium text-neutral-50">
                  {attributedMember.name || attributedMember.email}
                </p>
                {attributedMember.name && (
                  <p className="text-xs text-neutral-400">{attributedMember.email}</p>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3 text-neutral-500">
              <div className="w-10 h-10 rounded-full bg-neutral-700 flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <p className="text-sm">Não atribuído</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactName;
